import { FC } from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import { CloseIcon } from '../../../../../components/Icons/CloseIcon';
import { ArrowBackIcon } from '../../../../../components/Icons/ArrowBackIcon';

import styles from './FlightDelayPrediction.module.scss';
import { useGetFlightDelayPredictionQuery } from '../../../../../store/services/tripsApi';
import DelayChart from './components/DelayChart/DelayChart';

interface FlightDelayPredictionProps {
  activityId: number;
  onBack: () => void;
  onClose: () => void;
}

const FlightDelayPrediction: FC<FlightDelayPredictionProps> = ({ activityId, onBack, onClose }) => {
  const { data: predictions, isLoading, error, refetch } = useGetFlightDelayPredictionQuery(activityId);

  const handleRetry = () => {
    refetch();
  };

  const getErrorMessage = () => {
    try {
      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = (error as any).data;

        if (errorData?.type === 'validation_error' && errorData?.errors && Array.isArray(errorData.errors)) {
          const errorMessages = errorData.errors
            .map((err: any) => err.detail)
            .filter((detail: string) => detail && detail.trim() !== '');

          if (errorMessages.length > 0) {
            return errorMessages.join('. ');
          }
        }
        if (errorData?.message) {
          return errorData.message;
        }

        if (errorData?.detail) {
          return errorData.detail;
        }
      }

      if (error && typeof error === 'object' && 'message' in error) {
        return (error as any).message;
      }
    } catch (e) {
      console.warn('Error parsing error message:', e);
    }

    return 'Unable to load flight delay prediction';
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box className={styles.loadingContainer}>
          <CircularProgress size={40} />
          <Typography
            variant="h10"
            sx={{ color: '#5E6A81', mt: 2 }}
          >
            Analyzing flight data...
          </Typography>
        </Box>
      );
    }

    if (error) {
      const errorMessage = getErrorMessage();

      return (
        <Box className={styles.errorContainer}>
          <Typography
            variant="h9"
            sx={{ color: '#E74C3C', mb: 2 }}
          >
            {errorMessage}
          </Typography>
          <button
            className={styles.retryButton}
            onClick={handleRetry}
          >
            Try Again
          </button>
        </Box>
      );
    }

    if (!predictions || predictions.length === 0) {
      return (
        <Box className={styles.emptyContainer}>
          <Typography
            variant="h10"
            sx={{ color: '#5E6A81' }}
          >
            No prediction data available for this flight
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Typography
          variant="h10"
          className={styles.description}
        >
          We analyze real-time and historical data to show the probability of a delay.
        </Typography>

        <Box className={styles.chartSection}>
          <Typography
            variant="h10Bold"
            className={styles.chartTitle}
          >
            Probability
          </Typography>

          <DelayChart predictions={predictions} />
        </Box>

        <Typography variant="h10">
          Delay risk is categorized into four ranges — from minor delays (up to 30 minutes) to major disruptions (over 2
          hours or cancellation)
        </Typography>
      </>
    );
  };

  return (
    <Box className={styles.flightDelayPrediction}>
      <Box className={styles.header}>
        <IconButton
          onClick={onBack}
          className={styles.backButton}
          size="small"
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography
          variant="h7Bold"
          className={styles.title}
        >
          Flight Delay Prediction
        </Typography>

        <IconButton
          onClick={onClose}
          className={styles.closeButton}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={styles.content}>
        <Typography
          variant="h9Bold"
          className={styles.subtitle}
        >
          Check if your flight might be delayed
        </Typography>

        {renderContent()}
      </Box>
    </Box>
  );
};

export default FlightDelayPrediction;
